'use client'

import { useContext, useState, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/shadcn/dialog'
import { Button } from '@/components/ui/shadcn/button'
import { Input } from '@/components/ui/shadcn/input'
import { Label } from '@/components/ui/shadcn/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/shadcn/select'
import { Separator } from '@/components/ui/shadcn/separator'
import { AlertTriangle } from 'lucide-react'
import ErrorAlerts from '../feedback/ErrorAlerts'
import { fetcher } from '@/lib/api/fetch'
import { AuthContext } from '@/contexts/AuthContext'
import { ManagerContext } from '@/contexts/ManagerContext'
import { SettingsContext } from '@/contexts/SettingsContext'

export default function MemberModal({
  open,
  setOpen,
  organization,
  member,
  setMember,
}) {
  const context = useContext(ManagerContext)
  const authContext = useContext(AuthContext)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [errors, setErrors] = useState(null)
  const [submitDisabled, setSubmitDisabled] = useState(false)
  const [userGroup, setUserGroup] = useState('')
  const [username, setUsername] = useState('')
  const settingsContext = useContext(SettingsContext)

  useEffect(() => {
    if (member) setUserGroup(member.userGroup)
    else setUserGroup('')
  }, [member])

  // No point in a submit if nothing has changed
  useEffect(() => {
    if (member) {
      setSubmitDisabled(member.userGroup === userGroup)
      setUsername(member.email)
    }
  }, [member, userGroup])

  const handleResponse = async (res, handleOrg) => {
    const data = await res.json()
    setLoading(false)

    if (res.ok) {
      setError(null)
      setErrors(null)
      handleOrg(organization.id, data.member, true)
      setMember(null)
      setUsername(null)
      setUserGroup(null)
      setOpen(false)
    } else if (data.message) setError(data.message)
    else setErrors(data.messages)
  }

  const submit = async e => {
    e.preventDefault()
    setLoading(true)

    const res = await fetcher(
      `/api/admin/organization/${organization.id}/member`,
      {
        method: 'POST',
        body: JSON.stringify({
          email: e.target.email.value,
          userGroup: e.target.userGroup.value,
        }),
      },
      authContext,
    )

    await handleResponse(res, context.addMemberToOrganization)
  }

  const updateMember = async e => {
    e.preventDefault()
    setLoading(true)

    const res = await fetcher(
      `/api/admin/organization/${organization.id}/member/${member.memberId}`,
      {
        method: 'POST',
        body: JSON.stringify({ userGroup: e.target.userGroup.value }),
      },
      authContext,
    )

    await handleResponse(res, context.updateMemberInOrganization)
  }

  const handleClose = () => {
    setOpen(false)
    setMember(null)
    setUserGroup(null)
    setUsername(null)
  }

  return (
    <Dialog
      open={open}
      onOpenChange={v => {
        if (!v) handleClose()
      }}
    >
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {member ? 'Update Member' : 'Invite a New Member'}
          </DialogTitle>
          <DialogDescription>
            {!member ? (
              'Add an email address of the new member, choose a user group and click "Send Invite" to give access to your organization.'
            ) : (
              <>
                To update <strong>{member.name || member.email}</strong> make
                your change and click the &quot;Update Member&quot; button
              </>
            )}
          </DialogDescription>
        </DialogHeader>
        <Separator />
        <ErrorAlerts
          error={error}
          errors={errors}
          message="There was an error inviting the member."
          useGrid
        />
        <form onSubmit={member ? updateMember : submit} className="space-y-4">
          {!member && (
            <div className="space-y-2">
              <Label htmlFor="email">Member Email</Label>
              <Input
                id="email"
                name="email"
                type="email"
                disabled={!!member}
                onChange={e => setUsername(e.target.value)}
                value={username}
                autoFocus
                required
              />
            </div>
          )}
          <div className="space-y-2">
            {!settingsContext.userGroups?.length && (
              <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-100">
                <AlertTriangle className="size-4 mt-0.5 shrink-0" />
                Add a User Group below to invite a new member.
              </div>
            )}
            {!!settingsContext.userGroups?.length && (
              <>
                <Label>User Group</Label>
                <Select value={userGroup || ''} onValueChange={setUserGroup}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select user group..." />
                  </SelectTrigger>
                  <SelectContent>
                    {settingsContext.userGroups.map(group => (
                      <SelectItem key={group.id} value={group.id}>
                        {group.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <input type="hidden" name="userGroup" value={userGroup || ''} />
              </>
            )}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitDisabled || !userGroup || !username || loading}
            >
              {member ? 'Update Member' : 'Send Invite'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
